export const FOOD_SPIKES = ['high', 'medium', 'stable', 'low'] as const;
export type FoodSpikeType = (typeof FOOD_SPIKES)[number];

export const MEAL_TIMINGS = [
  'breakfast',
  'lunch',
  'afternoon',
  'dinner',
  'midnight',
] as const;
export type MealTiming = (typeof MEAL_TIMINGS)[number];

interface MacroSplit {
  fat: number;
  carbs: number;
  fiber: number;
  protein: number;
  calories: number;
}

interface MacroSplitData {
  id: string;
  name: string;
  macro_split: MacroSplit | null;
}

type MealTimingCounts = Partial<Record<MealTiming, number>>;

export interface FoodSummary {
  food_id: number;
  name: string;
  url_friendly_name: string;
  average_score: number;
  average_peak_glucose_levels: number;
  spike_indicator: FoodSpikeType | '';
  meal_timing_to_show_at: MealTiming | '';
}

export interface FoodDetailDataType extends FoodSummary {
  average_glucose_change: number;
  average_time_out_of_target: number;
  country_code_most_logged: string;
  meal_timing_most_logged: string;
  meal_timing_counts: MealTimingCounts;
  country_code_to_show_in: string;
  median_score: number;
  spike_subtitle: string;
  percentage_stable_response: number;
  percentage_small_rise: number;
  percentage_medium_spike: number;
  percentage_high_spike: number;
  macro_split_data: MacroSplitData[];
  times_logged: number;
  unique_users_count: number;
  grouped_meals_count: number;
}

export interface OptimisationItem {
  title: string | null;
  description: string;
}

export interface MealOptimisation {
  title: string;
  items: OptimisationItem[];
  disclaimerTitle: string;
  disclaimerText: string;
}

export type FoodRef = { foodId: string } | { urlFriendlyName: string };

export type FoodLookup =
  | { kind: 'found'; food: FoodDetailDataType }
  | { kind: 'not-found' }
  | { kind: 'error' };

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const toNumber = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const MAX_PARAM_LENGTH = 200;

export function readSpike(v: unknown): FoodSpikeType | '' {
  return typeof v === 'string' &&
    (FOOD_SPIKES as readonly string[]).includes(v)
    ? (v as FoodSpikeType)
    : '';
}

export function readMeal(v: unknown): MealTiming | '' {
  return typeof v === 'string' &&
    (MEAL_TIMINGS as readonly string[]).includes(v)
    ? (v as MealTiming)
    : '';
}

// proxy.ts rewrites through `new URL()`. That decodes unreserved ASCII
// (%2D, %32) and leaves reserved bytes (%2B) and non-ASCII sequences
// encoded. The page receives that mix. generateMetadata gets it fully
// decoded. One decode covers the leftovers and does nothing to the rest.
export const normalizeFoodParam = (raw: string): string | null => {
  let value = raw;
  try {
    value = decodeURIComponent(raw);
  } catch {
    // A lone % (already-decoded "100%") is not an escape. Keep it.
  }
  if (/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]/u.test(value)) return null;
  value = value.trim().normalize('NFKC').toLowerCase();
  return value && value.length <= MAX_PARAM_LENGTH ? value : null;
};

export const toFoodRef = (value: string): FoodRef =>
  /^[1-9]\d{0,9}$/.test(value) ? { foodId: value } : { urlFriendlyName: value };

export const suggestionQuery = (value: string): string => {
  const first = value.split('-')[0];
  return /^\d*$/.test(first) ? '' : first;
};

export function toFoodSummary(raw: unknown): FoodSummary | null {
  if (!isRecord(raw)) return null;
  const { name, url_friendly_name: slug } = raw;
  if (typeof name !== 'string' || !name) return null;
  const foodId = toNumber(raw.food_id);
  const urlFriendlyName = typeof slug === 'string' ? slug : '';
  if (!urlFriendlyName && !foodId) return null;
  return {
    food_id: foodId,
    name,
    url_friendly_name: urlFriendlyName,
    average_score: toNumber(raw.average_score),
    average_peak_glucose_levels: toNumber(raw.average_peak_glucose_levels),
    spike_indicator: readSpike(raw.spike_indicator),
    meal_timing_to_show_at: readMeal(raw.meal_timing_to_show_at),
  };
}
